import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toDate } from "../../../utils/toDate";

import useLanguage from "../../../utils/useLanguage";
import useRequest from "../../../utils/useRequest";

import { toMoney } from "../../../utils/toMoney";
import { sanitizeNumber } from "../../../utils/sanitizeNumber";

import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../../components/table-component/RoundedTable";

import printIcon from "../../../assets/printer_icon.png";
import archiveIcon from "../../../assets/archive_icon.png";
import editIcon from "../../../assets/edit_icon.png";
import saveIcon from "../../../assets/save_icon.png";

import EditItemInvoiceModal from "./EditItemInvoiceModal";
import PreviewItemInvoiceModal from "./PreviewItemInvoiceModal";
import TextDiv from "../../../components/TextDiv";
import CustomButton from "../../../components/CustomButton";
import BalanceBoxes from "../../../components/BalanceBoxes";
import AreYouSureModal from "../../../components/AreYouSureModal";

export default function ItemInvoiceBody({ data, setData, mode, handlePrint }) {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const [editMode, setEditMode] = useState(false);
  const [archivedModal, setArchivedModal] = useState(false);

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleConfirmEdit = async () => null;

  const handleToggleModal = () => {
    setEditMode(false);
    // setError("");
  };

  const finalAmountInWords =
    currentDirection === "rtl" ? data?.finalAmountInAr : data?.finalAmountInEn;

  const tableTitles = [
    { value: "sequence" },
    { value: "productName" },
    { value: "numberOfBoxes" },
    { value: "price" },
    { value: "length" },
    { value: "width" },
    { value: "totalPrice" },
  ];

  const tableRows = data?.items?.map((item, index) => [
    {
      value: index + 1,
      type: TABLE_CELL_TYPES.TEXT,
    },

    {
      value: item?.name,
      type: TABLE_CELL_TYPES.TEXT,
    },
    {
      value: item?.quantity,
      type: TABLE_CELL_TYPES.TEXT,
    },
    {
      value: item?.price,
      type: TABLE_CELL_TYPES.MONEY,
    },

    {
      value: item?.width,
      type: TABLE_CELL_TYPES.TEXT,
    },

    {
      value: item?.height,
      type: TABLE_CELL_TYPES.TEXT,
    },

    {
      value: item?.totalPrice,
      type: TABLE_CELL_TYPES.MONEY,
    },
  ]);

  const payload = {
    // Invoice data
    id: data?.id,
    date: data?.date,
    notes: data?.notes,
    receivedAmount: parseFloat(sanitizeNumber(data?.receivedAmount)),
    leftAmount: parseFloat(sanitizeNumber(data?.leftAmount)),
    discount: parseFloat(sanitizeNumber(data?.discount)),
    items: data?.items,
    invoiceType: data?.invoiceType,
    totalPrice: parseFloat(sanitizeNumber(data?.totalPrice)),
    finalAmount: parseFloat(sanitizeNumber(data?.finalAmount)),
    finalAmountInAr: data?.finalAmountInAr,
    finalAmountInEn: data?.finalAmountInEn,

    // Customer data
    customerName: data?.customer?.name,
    customerPhoneNumber: data?.customer?.phoneNumber,
    customerAddress: data?.customer?.address,
    customerId: data?.customer?.id,

    // Ids
    userId: parseInt(data?.userId),
    organizationId: parseInt(data?.organizationId),
  };

  // * Save invoice (Preview page)
  const {
    data: createdInvoice,
    loading,
    error,
    refetch: sendPostRequest,
  } = useRequest(`/createItemInvoice`, "POST", payload, false, false);

  // * ArchiveInvoice
  const {
    loading: archiveLoading,
    error: errorLoading,
    success: successfullyArchived,
    refetch: sendDeleteRequest,
  } = useRequest(`/archiveItemInvoice/${data?.id}`, "DELETE", {}, null, false);

  useEffect(() => {
    if (successfullyArchived) {
      setArchivedModal(false);
      setData((prev) => ({ ...prev, archived: true }));
    }
  }, [successfullyArchived]);

  const handleCreateInvoice = async () => {
    sendPostRequest();
  };

  const handleToggleArchiveModal = () => {
    setArchivedModal((prev) => !prev);
  };

  const handleConfirmArchive = () => {
    sendDeleteRequest();
  };

  return (
    <div className="o-column u-padding--bottom_500">
      {/* Body section 1 */}
      <section className="item-invoice">
        {/* Column 1 */}
        <div>
          <TextDiv
            title={"customerName"}
            value={data?.customer?.name}
            divClasses="item-invoice-div"
            spanClasses="u-flex_flex-2"
          />

          <TextDiv
            title={"phoneNumber"}
            value={data?.customer?.phoneNumber}
            divClasses="item-invoice-div"
            spanClasses="u-flex_flex-2"
          />

          <TextDiv
            title={"address"}
            value={data?.customer?.address}
            divClasses="item-invoice-div"
            spanClasses="u-flex_flex-2"
          />
        </div>

        {/* Column 2 */}
        <div>
          <TextDiv
            title={"invoiceNumber"}
            value={data?.id}
            divClasses="item-invoice-div"
          />
          <TextDiv
            title={"invoiceType"}
            value={t(data?.invoiceType)}
            divClasses="item-invoice-div"
          />
          <TextDiv
            title={"Date"}
            value={toDate(data?.date)}
            divClasses="item-invoice-div"
          />
        </div>
      </section>

      {/* Body section 2 */}
      <section className="u-margin--top_500 u-margin--bottom_500">
        <RoundedTable titles={tableTitles} rows={tableRows} />
      </section>

      {/* Body section 3 */}
      <section className="u-margin--top_500 u-margin--bottom_500">
        <TextDiv
          title={t("notes")}
          value={data?.notes || t("noNotes")}
          divClasses="item-invoice-div o-row--vertical-align_center"
        />
      </section>

      {/* Body section 4 */}
      <section className="o-row o-row--horizontal-align_space-between u-width_full-width u-margin--top_500 u-margin--bottom_500">
        <BalanceBoxes
          notAlignedEnd={true}
          boxes={[
            { title: "discount", value: `${data?.discount} %` },
            {
              title: "received",
              value: `${data?.receivedAmount} ${
                data?.customer?.currency || t("IQD")
              }`,
            },
            {
              title: "left",
              value: `${data?.leftAmount} ${
                data?.customer?.currency || t("IQD")
              }`,
            },
          ]}
        />
        <BalanceBoxes
          boxes={[
            {
              title: "totalAmount",
              value: `${toMoney(data?.totalPrice)} ${
                data?.customer?.currency || t("IQD")
              }`,
            },
            {
              title: "netAmount",
              value: `${toMoney(data?.finalAmount)} ${
                data?.customer?.currency || t("IQD")
              }`,
            },
            { title: "netAmount", value: finalAmountInWords },
          ]}
        />
      </section>

      {/* Body section 5 -> Actions */}
      <section className="o-row o-row--horizontal-align_end o-row--gap-size_500 u-margin--top_500">
        {/* View action buttons */}
        {mode === "view" && !editMode ? (
          <>
            {!data?.archived && (
              <CustomButton
                title={t("archive")}
                variant="red"
                icon={archiveIcon}
                iconSize={20}
                onClick={handleToggleArchiveModal}
              />
            )}
            <CustomButton
              title={t("print")}
              onClick={handlePrint}
              variant="gray"
              icon={printIcon}
              iconSize={18}
            />

            <CustomButton
              variant="blank"
              title={t("edit")}
              onClick={handleEdit}
              icon={editIcon}
              iconSize={18}
            />
          </>
        ) : null}

        {/* Preview action buttons */}
        {mode === "preview" && !editMode ? (
          <>
            {!data?.archived && (
              <CustomButton
                title={t("cancel")}
                variant="red"
                icon={archiveIcon}
                iconSize={20}
                // onClick={() => setShowArchiveModal(true)}
              />
            )}
            <CustomButton
              variant="blank"
              title={t("edit")}
              onClick={handleEdit}
              icon={editIcon}
              iconSize={18}
            />
            <CustomButton
              title={t("save")}
              onClick={handleCreateInvoice}
              icon={saveIcon}
              iconSize={16}
              // disabled={loading}
            />
          </>
        ) : null}

        {mode === "preview" && editMode && (
          <PreviewItemInvoiceModal
            handleToggleModal={handleToggleModal}
            data={data}
            setData={setData}
          />
        )}

        {mode === "view" && editMode && (
          <EditItemInvoiceModal
            handleToggleModal={handleToggleModal}
            data={data}
            setData={setData}
          />
        )}
      </section>

      {/* Archive modal -> Are you sure */}
      {archivedModal && (
        <AreYouSureModal
          onClose={handleToggleArchiveModal}
          onConfirm={handleConfirmArchive}
          closeTitle="cancel"
          confirmTitle="confirm"
          loading={archiveLoading}
          confirmIconType="whiteCheck"
        />
      )}
    </div>
  );
}
